import React from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import DangerZone from '../components/DangerZone';
import { useAuth } from '../contexts/AuthContext';
import { apiPut } from '../lib/api';
import { useUrlTab } from '../lib/hooks/useUrlState';
import {
  Building2, Users, Bell, Workflow, Database, FileText, Shield, ShieldAlert, ShieldCheck,
  Save, ArrowRight, Clock, BookOpenCheck, MapPin, AlertOctagon, Mail, HardDrive, Layout, Layers,
  Briefcase, MessageSquare, ServerCog, Sparkles, Search, ListChecks, BarChart3, CalendarClock, Car, Smartphone, Upload, Activity
} from 'lucide-react';

const LEGACY_ROLES = ['MD', 'PM', 'Site Engineer', 'Procurement', 'Finance', 'HR'];
const ROLE_DESCRIPTIONS = {
  MD: 'Full access to all 17 modules — read & write.',
  PM: 'Read all operational modules · write to Projects, DPR, Quality, HSE, Subcontractors, Documents.',
  'Site Engineer': 'Mobile-first access · write DPR, Quality observations and HSE incidents.',
  Procurement: 'MPR/RFQ/PO workflow · vendors and subcontractor master · inventory.',
  Finance: 'AP/AR aging · P&L · Cash flow · Billing · Contracts · approvals.',
  HR: 'Employee master · attendance · payroll · leave management.',
  'Business Development': 'CRM · Pipeline · Companies · Contacts · Lead Tracker · Reports.',
  Tendering: 'Tender Register · Rate Library · BOQ Preparation · Procurement coordination.',
  Engineering: 'Drawings · BOQ · RFI · Submittals · MSP · Project execution support.',
  'Finance & Accounts': 'GL · Invoices · Payments · Contracts · Statutory · Reports.',
  Admin: 'HR · Labour · Documents · System settings · User management.',
  'Projects/Site': 'Site execution · DPR · Tasks · Labour · Equipment · Quality / HSE observations.',
  Quality: 'NCR · Snags · Tests · Inspections · Project quality dashboard.',
  Safety: 'Incidents · Toolbox talks · Permits · HSE inspections.',
  'Contracts & Legal': 'Contracts · Variation Orders · Claims · Disputes · Correspondence.',
  IT: 'System settings · User management · Audit log · Notifications.',
};

// 20 settings pages consolidated into 7 logical groups for easier navigation.
const GROUPS = [
  {
    id: 'organisation',
    title: 'Organisation',
    icon: Briefcase,
    description: 'Company identity, addresses, and document numbering.',
    tiles: [
      { icon: Building2, title: 'Company Profile', desc: 'Logo, GST, PAN, address and bank details.', to: '/app/settings/company-profile' },
      { icon: Building2, title: 'Multi-Company Management', desc: 'Manage Ellora EPC + Ellora Construction · grant cross-company access · transfer employees.', to: '/app/settings/companies' },
      { icon: MapPin, title: 'Office Locations', desc: 'Offices with GPS coordinates & geofence radius for mobile attendance.', to: '/app/settings/office-locations' },
      { icon: FileText, title: 'Number Series', desc: 'Auto-numbering for PO, GRN, Invoice, WO, NCR, RA Bills.', to: '/app/settings/number-series' },
    ],
  },
  {
    id: 'people',
    title: 'People & Access',
    icon: Users,
    description: 'Users, roles, security, deletion approvals.',
    tiles: [
      { icon: Users, title: 'User Management', desc: 'Add users, assign roles, review approval queue.', to: '/app/user-management' },
      { icon: ShieldAlert, title: 'Roles & Access Control', desc: 'Module × action permission matrix. Edit roles or create custom ones.', to: '/app/settings/roles' },
      { icon: ShieldAlert, title: 'Project Scoping', desc: 'Control which projects each employee can access. Toggle strict enforcement once all employees are assigned.', to: '/app/settings/access-control' },
      { icon: ShieldCheck, title: 'Security Center', desc: 'Active sessions, login history, rate-limit hits, force-logout.', to: '/app/settings/security' },
      { icon: AlertOctagon, title: 'Deletion Approvals', desc: 'Major-entity deletes (projects, employees, payroll) require RM approval.', to: '/app/settings/deletion-requests' },
    ],
  },
  {
    id: 'workflows',
    title: 'Workflows & Forms',
    icon: Workflow,
    description: 'Approval chains, form-level config, onboarding wizard, SLA rules.',
    tiles: [
      { icon: Workflow, title: 'Approval Workflows', desc: 'Configure PO, invoice, leave, RA bill approval chains.', to: '/app/settings/approval-workflows' },
      { icon: Layout, title: 'Form Field Configuration', desc: 'Mark fields required, optional or hidden across any form.', to: '/app/settings/form-fields' },
      { icon: Clock, title: 'ERP Configuration · Master Lists', desc: 'Every master dropdown list used across the ERP, including the lead and opportunity stage lists.', to: '/app/settings/crm-sla' },
      { icon: BookOpenCheck, title: 'Onboarding Wizard', desc: 'Enable / disable post-login onboarding wizard.', to: '/app/settings/onboarding' },
      { icon: ListChecks, title: 'IT Onboarding Checklist', desc: 'Configure the per-joiner IT provisioning steps (laptop, email, SIM, access).', to: '/app/settings/it-onboarding-checklist' },
    ],
  },
  {
    id: 'comms',
    title: 'Communications',
    icon: MessageSquare,
    description: 'Email accounts, notification preferences, subscriptions, and diagnostics.',
    tiles: [
      { icon: Mail, title: 'Email Configuration', desc: 'SMTP profiles + per-use-case mailboxes (HR, Procurement, Payroll).', to: '/app/settings/email' },
      { icon: Bell, title: 'Notification Preferences', desc: 'Per-user opt-in / opt-out for event emails.', to: '/app/settings/notification-preferences' },
      { icon: Upload, title: 'File Upload Limits', desc: 'Per-segment size caps for BD, Tendering, HR, and more. Super admin only.', to: '/app/settings/upload-limits', adminOnly: true },
      { icon: CalendarClock, title: 'Email Subscriptions', desc: 'Master control for every scheduled email — toggles, IST send time, frequency, role groups and guaranteed subscribers.', to: '/app/settings/email-subscriptions' },
      { icon: Bell, title: 'Notification Subscriptions', desc: 'Per-event admin override — add/exclude specific email addresses, master kill-switch, test-send. Use this when an executive isn\'t getting an alert and you don\'t want to change their HR record.', to: '/app/settings/notification-subscriptions' },
      { icon: Search, title: 'Daily Email Diagnostics', desc: 'See who receives the daily digests and why someone is missing — with one-click re-enable & test send.', to: '/app/settings/email-diagnostics' },
    ],
  },
  {
    id: 'masters',
    title: 'Data Masters',
    icon: Layers,
    description: 'Configurable master lists driving the entire ERP.',
    tiles: [
      { icon: Layers, title: 'Master Lists · with Semantics', desc: 'Attendance codes, leave types, departments, PQ categories, etc.', to: '/app/settings/master-lists' },
      { icon: FileText, title: 'Petty Cash Categories', desc: 'Editable list of categories shown in the petty-cash expense form (web + mobile).', to: '/app/settings/petty-cash-categories' },
      { icon: FileText, title: 'Stores Configuration', desc: 'Non-PO GRN value limit for petty purchases (e.g. nails, consumables).', to: '/app/settings/stores-config' },
      { icon: FileText, title: 'Procurement · T&C Library', desc: 'Standard Terms & Conditions clause sets to pick when awarding an RFQ or raising a Direct PO.', to: '/app/settings/tc-library' },
    ],
  },
  {
    id: 'storage',
    title: 'Storage & Integrations',
    icon: HardDrive,
    description: 'Backups, exports, and cloud-storage connections.',
    tiles: [
      { icon: HardDrive, title: 'Database Backups', desc: 'Daily automatic snapshots @ 02:00 IST · download / delete · last 30 retained.', to: '/app/settings/backups' },
      { icon: Database, title: 'Data Export & Import', desc: 'Download any module\'s collection as JSON, take a full off-site backup, or import data from another environment.', to: '/app/settings/data-export' },
      { icon: HardDrive, title: 'Google Drive Integration', desc: 'Store large tender / project PDFs in your own Drive. Zero server bandwidth.', to: '/app/settings/drive' },
      { icon: MapPin, title: 'GPS Tracking', desc: 'Vehicle GPS webhook key, idle-alert threshold, active hours & unknown-device review.', to: '/app/settings/gps' },
      { icon: Car, title: 'Fuel · RC Lookup', desc: 'Invincible Ocean RTO API — auto-fill vehicle make/model/fuel type/insurance/PUC from registration number.', to: '/app/settings/fuel-rc-lookup' },
      { icon: Clock, title: 'Labour Shift Model', desc: 'Standard shift hours, overtime blocks and multiplier, daily cap and kiosk block-after-max.', to: '/app/settings/labour-shift' },
      { icon: Clock, title: 'Attendance Hours', desc: 'Day-status thresholds (Present/Half Day/Absent) and the monthly short-leave quota. IT / Super Admin only.', to: '/app/settings/attendance-hours', itOnly: true },
      { icon: FileText, title: 'Salary Sheet Column Mapping', desc: 'Map HR\'s salary-sheet columns (Basic Pay, HRA, System Allowance, Statutory Bonus) to Salary Master components, then export the monthly sheet from Payroll.', to: '/app/settings/salary-sheet-mapping' },
      { icon: BarChart3, title: 'Analytics & BI · Power BI', desc: 'Issue long-lived tokens so Power BI / Excel can pull live dimensional data (projects, costs, procurement, HR, safety, tendering).', to: '/app/settings/analytics' },
    ],
  },
  {
    id: 'system',
    title: 'System Health',
    icon: ServerCog,
    description: 'Audit trails and system observability.',
    tiles: [
      { icon: Shield, title: 'Audit Log', desc: 'Full system audit trail — who did what, when, from where.', to: '/app/settings/audit-log' },
      { icon: BarChart3, title: 'User Analytics', desc: 'ERP adoption, module usage, login trends, inactive users. Super admin only.', to: '/app/settings/user-analytics' },
      { icon: Sparkles, title: 'AI Configuration', desc: 'Globally enable / disable every AI-powered feature in the platform. Super admin only.', to: '/app/settings/ai' },
      { icon: Smartphone, title: 'App Version', desc: 'Control in-app update prompts — set latest/min version code, force-update toggle, release notes, Play Store URL.', to: '/app/settings/app-version' },
      { icon: Activity, title: 'System Health · Performance', desc: 'Live response times, busiest hours, and DB growth trends — spot slowdowns before they become outages.', to: '/app/settings/system-health', adminOnly: true },
    ],
  },
];

export default function Settings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeGroup, setActiveGroup] = useUrlTab('group', 'organisation');

  const canSelfSwitchRole = user?.role === 'super_admin';

  const currentGroup = GROUPS.find((g) => g.id === activeGroup) || GROUPS[0];

  return (
    <div>
      <PageHeader overline="Settings & Administration" title="System Configuration" subtitle="Manage company profile, users, workflows and integrations." />

      <div className="bg-white border border-slate-200 rounded-sm p-5 mb-6">
        <div className="kpi-overline mb-3">Signed in as</div>
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-sm bg-[#2F3E46] text-white flex items-center justify-center font-display font-bold text-xl overflow-hidden shrink-0">
            {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : (user?.name || 'U').slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="font-display font-bold text-lg">{user?.name}</div>
            <div className="text-sm text-slate-500">{user?.email}</div>
            <div className="text-[10px] uppercase tracking-widest font-display text-[#3b4872] mt-1">Current role · {user?.role}</div>
          </div>
          {canSelfSwitchRole && (
            <button
              onClick={async () => {
                localStorage.removeItem('ellora_onboarding_dismissed');
                localStorage.removeItem('ellora_onboarding_status');
                try { await apiPut('/settings/onboarding', { admin_wizard_dismissed: false }); } catch { /* ignore */ }
                window.location.href = '/dashboard';
              }}
              data-testid="relaunch-onboarding-btn"
              className="text-xs text-[#3b4872] border border-[#3b4872]/30 rounded-sm px-3 py-1.5 hover:bg-indigo-50 font-display font-semibold"
            >
              Relaunch Onboarding Wizard
            </button>
          )}
        </div>
      </div>

      {/* Group tabs */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="settings-group-tabs">
        {GROUPS.map((g) => (
          <button
            key={g.id}
            type="button"
            onClick={() => setActiveGroup(g.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-sm border text-xs font-display font-bold uppercase tracking-wider transition-colors ${activeGroup === g.id ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-700 border-slate-300 hover:border-[#3b4872]'}`}
            data-testid={`settings-group-${g.id}`}
          >
            <g.icon size={14} /> {g.title}
            <span className="text-[9px] opacity-70">({g.tiles.length})</span>
          </button>
        ))}
      </div>

      {/* Group description */}
      <div className="mb-4 text-xs text-slate-600 italic flex items-center gap-2">
        <currentGroup.icon size={14} className="text-[#3b4872]" />
        {currentGroup.description}
      </div>

      {/* Tile grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {currentGroup.tiles.filter(t => (!t.adminOnly || user?.role === 'super_admin') && (!t.itOnly || user?.role === 'super_admin' || user?.department === 'IT')).map(t => (
          <button
            key={t.title}
            type="button"
            onClick={() => navigate(t.to)}
            className="text-left bg-white border border-slate-200 rounded-sm p-5 hover:border-[#3b4872]/60 hover:shadow-sm cursor-pointer group transition-all"
            data-testid={`settings-tile-${t.title.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and').replace(/[·.]/g, '')}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-10 h-10 rounded-sm bg-indigo-50 text-[#3b4872] flex items-center justify-center group-hover:bg-[#3b4872] group-hover:text-white transition-colors"><t.icon size={18} /></div>
              <ArrowRight size={14} className="text-slate-300 group-hover:text-[#3b4872] group-hover:translate-x-0.5 transition-all" />
            </div>
            <div className="font-display font-bold text-base mb-1">{t.title}</div>
            <p className="text-sm text-slate-600 leading-relaxed">{t.desc}</p>
          </button>
        ))}

        {activeGroup === 'system' && (user?.role === 'super_admin' || user?.role === 'MD') && (
          <a
            href="/strategy-report"
            target="_blank"
            rel="noopener noreferrer"
            className="text-left bg-gradient-to-br from-[#3b4872] to-[#2f3a5d] text-white border border-[#3b4872] rounded-sm p-5 hover:shadow-md cursor-pointer group transition-all block"
            data-testid="settings-tile-strategy-report"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-10 h-10 rounded-sm bg-white/15 text-white flex items-center justify-center group-hover:bg-white/25 transition-colors">
                <BookOpenCheck size={18} />
              </div>
              <ArrowRight size={14} className="text-white/60 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
            </div>
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-white/70 mb-1">Board · Confidential</div>
            <div className="font-display font-bold text-base mb-1">ERP Scale-Readiness Report</div>
            <p className="text-sm text-white/80 leading-relaxed">
              Board-ready strategy page — ₹1,000 Cr → ₹5,000 Cr roadmap with module-level cost estimates.
            </p>
          </a>
        )}
      </div>

      {user?.role === 'super_admin' && <DangerZone />}
    </div>
  );
}
